import type { CrateItem, MissingCrateItem, OrderDetails } from '@/entities/order/model'

/** Tutorial order used for Steps 4 / 6–11 when Zebra barcode scanning is on. */
export const TUTORIAL_SCAN_ORDER_ID = '100001'

/** Progressive Order Details phases while the tutorial modal is open (BD-8595). */
export type TutorialScanPhase
  = | 'order-crates'
    | 'scanned-crates'
    | 'missing-sheet'
    | 'missing-success'
    | 'all-accounted'
    | 'order-details-status'

const ORDER_ID = TUTORIAL_SCAN_ORDER_ID

/**
 * Area-code → crate-type map required by getExpectedCrateItemsFromArrayBarcodes.
 * Without this, barcodes parse to zero rows and the UI shows Total crates 0/0.
 */
export const TUTORIAL_AREA_CODE_CRATE_TYPE_MAPPING: Record<string, string> = {
  '01': 'Ambient',
  '02': 'Bulk Ambient',
  '03': 'Bulk Chilled',
  '21': 'Chilled',
  '31': 'Frozen',
  '09': 'Hot Food',
}

/** 7 licence plates → Scan 0/7 on the Prepare Order card / Order Crates screen. */
const TUTORIAL_BARCODES = [
  `A${ORDER_ID}*01*001`, // Ambient
  `A${ORDER_ID}*02*001`, // Bulk Ambient
  `A${ORDER_ID}*03*001`, // Bulk Chilled
  `A${ORDER_ID}*03*002`, // Bulk Chilled
  `A${ORDER_ID}*21*001`, // Chilled
  `A${ORDER_ID}*31*001`, // Frozen
  `A${ORDER_ID}*09*001`, // Hot Food
] as const

function crateItem(barcode: string, crateType: string): CrateItem {
  const [, areaCode, crateNumber] = barcode.split('*')
  return {
    barcode,
    orderId: ORDER_ID,
    areaCode: areaCode ?? '',
    crateNumber: crateNumber ?? '',
    crateType,
  }
}

const AMBIENT = crateItem(TUTORIAL_BARCODES[0], 'Ambient')
const BULK_AMBIENT = crateItem(TUTORIAL_BARCODES[1], 'Bulk Ambient')
const BULK_CHILLED_1 = crateItem(TUTORIAL_BARCODES[2], 'Bulk Chilled')
const BULK_CHILLED_2 = crateItem(TUTORIAL_BARCODES[3], 'Bulk Chilled')
const CHILLED = crateItem(TUTORIAL_BARCODES[4], 'Chilled')
const FROZEN = crateItem(TUTORIAL_BARCODES[5], 'Frozen')
const HOT_FOOD = crateItem(TUTORIAL_BARCODES[6], 'Hot Food')

const ALL_SCANNED_EXCEPT_BULK_AMBIENT: CrateItem[] = [
  AMBIENT,
  BULK_CHILLED_1,
  BULK_CHILLED_2,
  CHILLED,
  FROZEN,
  HOT_FOOD,
]

const BULK_AMBIENT_MISSING: MissingCrateItem = {
  ...BULK_AMBIENT,
  reason: 'Crate merged',
}

const TUTORIAL_HS_FIELDS: OrderDetails['hsFields'] = {
  'Customer Name': 'Sarah Johnson',
  'Order Number': ORDER_ID,
  'Van Seq': 'Van 01 Seq 101',
  'orderType': 'Click & Collect',
  'Collection Window': '11 - 12pm',
  'Driver Name': 'Daniel Deliverer',
  'Ambient': '1',
  'Bulk Ambient': '1',
  'Bulk Chilled': '2',
  'Chilled': '1',
  'Frozen': '1',
  'Hot Food': '1',
  'Restricted': '1',
  'Emojis': '🥶🆔',
}

function baseTutorialScanOrder(status: string): OrderDetails {
  return {
    projectId: 'tutorial',
    destinationId: 'tutorial',
    installRef: `tutorial-${ORDER_ID}`,
    helloId: `tutorial-hello-${ORDER_ID}`,
    status,
    lastUpdateTime: new Date().toISOString(),
    lastTransitionTime: new Date().toISOString(),
    waitTimeStart: new Date().toISOString(),
    customerName: 'Sarah Johnson',
    orderId: ORDER_ID,
    isAcknowledged: true,
    hasNotified: [],
    orderActivationTime: new Date().toISOString(),
    hsFields: TUTORIAL_HS_FIELDS,
    areaCodeCrateTypeMapping: { ...TUTORIAL_AREA_CODE_CRATE_TYPE_MAPPING },
    customEventMetaData: {
      array_barcodes: TUTORIAL_BARCODES.join(','),
    },
    crateScan: {
      expectedBarcodes: [...TUTORIAL_BARCODES],
      scannedBarcodes: [],
      missingCrates: [],
    },
  }
}

/**
 * Order snapshot for a tutorial scan phase (mutated into React Query while the modal is open).
 */
export function buildTutorialScanPhaseOrder(phase: TutorialScanPhase): OrderDetails {
  switch (phase) {
    case 'order-crates':
      return {
        ...baseTutorialScanOrder('OnTheWay'),
        crateScan: {
          expectedBarcodes: [...TUTORIAL_BARCODES],
          scannedBarcodes: [],
          missingCrates: [],
        },
      }
    case 'scanned-crates':
      // Ambient scanned (green tick). Bulk Ambient stays outstanding + underlined so Step 7
      // can teach "tap an underlined row to mark missing" before the missing sheet opens.
      return {
        ...baseTutorialScanOrder('OnTheWay'),
        crateScan: {
          expectedBarcodes: [...TUTORIAL_BARCODES],
          scannedBarcodes: [AMBIENT],
          missingCrates: [],
        },
      }
    case 'missing-sheet':
    case 'missing-success':
      return {
        ...baseTutorialScanOrder('OnTheWay'),
        crateScan: {
          expectedBarcodes: [...TUTORIAL_BARCODES],
          scannedBarcodes: [AMBIENT],
          missingCrates: [],
        },
      }
    case 'all-accounted':
      return {
        ...baseTutorialScanOrder('Arrived'),
        crateScan: {
          expectedBarcodes: [...TUTORIAL_BARCODES],
          scannedBarcodes: ALL_SCANNED_EXCEPT_BULK_AMBIENT,
          missingCrates: [BULK_AMBIENT_MISSING],
        },
      }
    case 'order-details-status':
      return {
        ...baseTutorialScanOrder('Arrived'),
        crateScan: {
          expectedBarcodes: [...TUTORIAL_BARCODES],
          scannedBarcodes: ALL_SCANNED_EXCEPT_BULK_AMBIENT,
          missingCrates: [BULK_AMBIENT_MISSING],
        },
      }
  }
}

/** Dashboard OTW / partner cards: barcodes present, nothing scanned yet. */
export function buildTutorialDashboardScanOrder(base: OrderDetails): OrderDetails {
  return {
    ...base,
    areaCodeCrateTypeMapping: {
      ...base.areaCodeCrateTypeMapping,
      ...TUTORIAL_AREA_CODE_CRATE_TYPE_MAPPING,
    },
    customEventMetaData: {
      ...base.customEventMetaData,
      array_barcodes: TUTORIAL_BARCODES.join(','),
    },
    crateScan: {
      expectedBarcodes: [...TUTORIAL_BARCODES],
      scannedBarcodes: [],
      missingCrates: [],
    },
    hsFields: {
      ...base.hsFields,
      ...TUTORIAL_HS_FIELDS,
      'Customer Name': base.customerName || TUTORIAL_HS_FIELDS['Customer Name'],
      'Order Number': base.orderId,
    },
  }
}

/**
 * Arrived / Delivering dashboard cards: all crates accounted for so CTAs stay
 * "We're on it" / "Complete" (not Scan) during Acknowledge + Deliver steps.
 */
export function buildTutorialFullyScannedOrder(base: OrderDetails): OrderDetails {
  return {
    ...base,
    areaCodeCrateTypeMapping: {
      ...base.areaCodeCrateTypeMapping,
      ...TUTORIAL_AREA_CODE_CRATE_TYPE_MAPPING,
    },
    customEventMetaData: {
      ...base.customEventMetaData,
      array_barcodes: TUTORIAL_BARCODES.join(','),
    },
    crateScan: {
      expectedBarcodes: [...TUTORIAL_BARCODES],
      scannedBarcodes: ALL_SCANNED_EXCEPT_BULK_AMBIENT,
      missingCrates: [BULK_AMBIENT_MISSING],
    },
    hsFields: {
      ...base.hsFields,
      ...TUTORIAL_HS_FIELDS,
      'Customer Name': base.customerName || TUTORIAL_HS_FIELDS['Customer Name'],
      'Order Number': base.orderId,
    },
  }
}
